import React, {useId} from 'react'
import PropTypes from 'prop-types'
import {Flex, HStack, Text} from '@chakra-ui/react'
import {Component} from '@salesforce/commerce-sdk-react/components'
import PDCTitle from '../../../components/pdc-title'

/**
 * PDCTextBlock is a presentational React component designed to render a flexible text block section within a page designer context.
 *
 * This component displays an optional title, description, and a list of child components, all styled responsively based on the provided width prop.
 * The title is rendered using the PDCTitle component and supports custom heading levels and font colors. The description is rendered as HTML content.
 * Additional components can be rendered below the description, sourced from the first region's components array.
 *
 * Accessibility is considered by associating the section with the title via aria-describedby.
 *
 * Props:
 * @param {Object} props - The component props.
 * @param {string} [props.title] - The title text to display at the top of the block.
 * @param {string} [props.titleHeadingLevel] - The heading level for the title (e.g., 'h1', 'h2').
 * @param {string} [props.titleFontColor] - The color of the title text.
 * @param {string} [props.desc] - The HTML string to be rendered as the description below the title.
 * @param {string} [props.width] - The width setting for the block, determines responsive breakpoints ('small' or default).
 * @param {Array} [props.regions] - An array of region objects, where the first region's components are rendered below the description.
 *
 * @returns {JSX.Element} The rendered text block section, including title, description, and any child components.
 */
export const PDCTextBlock = ({title, titleHeadingLevel, titleFontColor, desc, width, regions}) => {
    let components = []
    if (regions) {
        components =
            regions?.find((region) => region.id === 'pdc_text_block_links')?.components || []
    }

    const titleId = useId()
    const responsiveWidths =
        width === 'small'
            ? ['320px', null, '500px', '600px']
            : ['320px', null, '500px', '800px', '1000px']

    return (
        <Flex
            as="section"
            aria-describedby={titleId}
            justifyContent={'center'}
            alignItems={'center'}
            p={['64px 10px', null, '120px 20px']}
        >
            <Flex
                flexDirection="column"
                justifyContent="center"
                alignItems={'flex-start'}
                width={responsiveWidths}
            >
                {title && (
                    <PDCTitle
                        title={title}
                        titleFontColor={titleFontColor}
                        titleHeadingLevel={titleHeadingLevel}
                        txtHorizontalPos={'left'}
                        id={titleId}
                        mb={'20px'}
                        size={'4xl'}
                    />
                )}
                {desc && (
                    <Text
                        dangerouslySetInnerHTML={{
                            __html: desc
                        }}
                        variant="bodyLarge2"
                        textAlign={'left'}
                        color="fullBlack"
                    />
                )}

                {components && components.length > 0 && (
                    <HStack pt={'20px'} gap={16}>
                        {components.map((component, index) => {
                            return <Component key={component?.id || index} component={component} />
                        })}
                    </HStack>
                )}
            </Flex>
        </Flex>
    )
}

PDCTextBlock.propTypes = {
    title: PropTypes.string.isRequired,
    titleHeadingLevel: PropTypes.string,
    titleFontColor: PropTypes.string,
    subTitle: PropTypes.string,
    desc: PropTypes.string,
    txtHorizontalPos: PropTypes.string,
    width: PropTypes.string,
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    )
}
